import type * as HealthInsuranceConfig from './superTopupConfig';

export interface IStuErrorCopy {
  title: string;
  body?: string;
}

export const STU_UNLISTED_ERROR_CODES = {
  PROPOSER_IDENTITY_MISMATCH: 'STU_PROPOSER_IDENTITY_MISMATCH',
} as const;

export const STU_NETWORK_ERROR = {
  title: "We couldn't load your details just now",
  body: 'Nothing you entered has been lost. Check your connection and try again.',
};

/** What the error screen needs: the copy, and the page "Try again" reopens. */
export interface IStuErrorScreenState {
  error?: IStuErrorCopy;
  retryTo?: string;
  /** A way forward other than retrying, e.g. Get fresh quotes. */
  action?: { label: string; to: string };
}

/** A quote whose HDFC add-on choices were erased (guide 2026-10-15 §8; the guide gives no copy). */
/**
 * When the proposal can be changed again, after a payment in progress (guide 2026-10-20, re-sending
 * the proposal on Proceed §3: `meta.retryAfterMs`; the guide gives no copy).
 */
export const stuProposalChangeableIn = (retryAfterMs: number) => {
  const minutes = Math.max(1, Math.ceil(retryAfterMs / 60_000));
  return `You can change your proposal again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`;
};

export const STU_QUOTE_UNPRICEABLE: IStuErrorCopy = {
  title: 'This quote can no longer be priced',
  body: 'Please get a fresh quote.',
};

/** A step of the flow opened with no quote in the session. */
export const STU_QUOTE_MISSING: IStuErrorCopy = {
  title: "We couldn't find your quote",
  body: 'Start again to pick your members and plan.',
};

/** Field messages. The proposal form and KYC word an unticked consent differently. */
export const STU_VALIDATION_COPY = {
  required: 'This is required',
  proposalConsent: 'Please tick to continue',
  kycConsent: 'Tick this to continue',
} as const;

/** Toast copy when the error code has none of its own. */
export const STU_TOAST_FALLBACK = {
  startPayment: 'Unable to start the payment. Please try again.',
  continue: 'Unable to continue. Please try again.',
} as const;

/** The error screen's way on from a quote that can't come back. */
export const STU_FRESH_QUOTE_LABEL = 'Get fresh quotes';

export const STU_GENERIC_ERROR: IStuErrorCopy = {
  title: 'Something went wrong',
  body: "A Benefits Consultant from Plum's Personal Insurance Team will reach out in the next 24 hours to help you with the steps.",
};

type StuErrorCodes = typeof HealthInsuranceConfig.STU_ERROR_CODES;
type StuErrorCodeValue = StuErrorCodes[keyof StuErrorCodes];

const KNOWN_ERROR_COPY: Record<StuErrorCodeValue, IStuErrorCopy> = {
  STU_PRICE_CHANGED: {
    title: 'The premium for this plan has changed',
    body: 'Review the new price before you continue.',
  },
  STU_PLAN_NOT_COMMITTED: {
    title: 'Pick your plan again to continue',
  },
  // Plan ids (guide 2026-10-14 §2): "not selectable" now only means Plum switched it off.
  STU_PLAN_NOT_SELECTABLE: {
    title: 'This plan is not available',
    body: 'Choose another plan to carry on.',
  },
  STU_PLAN_NO_LONGER_OFFERED: {
    title: 'This plan is no longer available',
  },
  STU_PLAN_ID_REPLACED: {
    title: 'This plan was re-quoted and has a new id',
  },
  STU_PLAN_LIST_UPDATING: {
    title: 'The plan list is being updated',
    body: 'Try again shortly.',
  },
  STU_PLAN_LIST_CHANGED: {
    title: 'The plan list has changed',
  },
  STU_PLAN_NOT_IN_QUOTE: {
    title: 'The plan list has changed',
  },
  STU_INSURER_PRICE_FAILED: {
    title: 'The insurer could not price this plan right now',
  },
  STU_ADDON_OPTION_REQUIRED: {
    title: 'Choose an option for this cover',
  },
  STU_ADDON_OPTION_INVALID: {
    title: 'That option is not offered for this cover',
  },
  STU_ADDON_COMBINATION_NOT_ALLOWED: {
    title: "This addon can't be added with your current selection",
  },
  STU_ADDON_NOT_APPLIED: {
    title: 'The insurer priced a different choice',
  },
  STU_KYC_REQUIRED: {
    title: 'Complete your KYC to continue',
    body: 'The insurer needs your identity verified before this proposal can go ahead.',
  },
  STU_PROPOSAL_COMPOSITION_CHANGED: {
    title: 'The insured members changed',
    body: 'This proposal cannot be continued. Start again to keep the same plan and price.',
  },
  STU_FORM_UNAVAILABLE: {
    title: 'The insurer is not responding',
    body: 'Nothing you entered has been lost. Try again in a moment.',
  },
  STU_PROPOSAL_NOT_FOUND: {
    title: "We couldn't find this application",
    body: 'Start a new quote to continue.',
  },
  STU_PROPOSAL_VERSION_CONFLICT: {
    title: 'This application was updated somewhere else',
    body: 'Check your answers and save again.',
  },
  STU_FORM_VERSION_CHANGED: {
    title: 'The insurer updated their questions',
    body: 'Review this page again before saving.',
  },
  STU_PROPOSAL_FROZEN: {
    title: 'Payment has already started',
    body: 'This proposal can no longer be edited.',
  },
  STU_ANSWER_INVALID: {
    title: 'Some answers need fixing',
    body: 'Nothing on this page was saved. Correct the marked answers and try again.',
  },
  STU_AFFIRMATION_NOT_PERMITTED: {
    title: 'This application cannot be confirmed yet',
    body: 'Finish the remaining questions first.',
  },
  STU_AFFIRMATION_STALE: {
    title: 'Your answers changed after you confirmed them',
    body: 'Review them once more and confirm again.',
  },
  STU_PROPOSAL_INCOMPLETE: {
    title: 'Some required answers are still missing',
  },
  STU_PROPOSAL_BLOCKED: {
    title: 'We cannot take this application online',
    body: 'A Benefits Consultant will call you to take it forward.',
  },
  STU_PROPOSAL_NOT_AFFIRMED: {
    title: 'Confirm your answers before submitting',
  },
  STU_PROPOSAL_PAYMENT_IN_PROGRESS: {
    title: 'A payment for this plan was started in the last 30 minutes',
    body: 'Complete it here.',
  },
  STU_PROPOSAL_SUBMIT_IN_PROGRESS: {
    title: 'This application is already being submitted',
    body: 'Give it a moment before trying again.',
  },
  STU_PROPOSAL_REJECTED: {
    title: 'The insurer could not accept this application',
    body: 'A Benefits Consultant will call you to talk through the options.',
  },
  STU_PROPOSAL_UNCONFIRMED: {
    title: 'The insurer has not confirmed this application yet',
    body: "We'll let you know as soon as they do.",
  },
  STU_D2C_PROFILE_REQUIRED: {
    title: 'We need a few details about you first',
    body: 'Complete your profile to carry on with this quote.',
  },
  STU_QUOTE_SESSION_FORBIDDEN: {
    title: 'This quote belongs to someone else',
    body: 'Start a new quote to continue.',
  },
  STU_INVALID_QUOTE_REQUEST: {
    title: "We couldn't price a plan with these details",
    body: 'Check the members and dates of birth you entered and try again.',
  },
  STU_QUOTE_SESSION_NOT_FOUND: {
    title: 'This quote has expired',
    body: 'Start again to see current prices.',
  },
  STU_QUOTE_NOT_RECALCULABLE: {
    title: "This quote can't be repriced",
    body: 'Start a new quote to see current prices.',
  },
  STU_PAYMENT_ATTEMPT_EXISTS: {
    title: 'A payment is already in progress',
    body: 'Wait for it to finish before starting another.',
  },
  STU_PAYMENT_LINK_UNAVAILABLE: {
    title: 'We could not open the payment page',
    body: 'Please try again in a few minutes.',
  },
  STU_VENDOR_REQUEST_FAILED: {
    title: 'The insurer did not respond',
    body: 'Please try again in a few minutes.',
  },
  STU_POLICY_DOCUMENT_NOT_READY: {
    title: 'Your policy document is still being prepared',
    body: "We'll email it to you as soon as it is ready.",
  },
  STU_PROPOSAL_NOT_SUBMITTED: {
    title: 'This application has not been submitted yet',
    body: 'Finish and submit it to continue.',
  },
  STU_PROPOSAL_AMENDED: {
    title: 'This application was changed after you confirmed it',
    body: 'Review the changes and confirm again.',
  },
  STU_PLAN_SUPERSEDED: {
    title: 'This plan has been replaced by a newer one',
    body: 'Pick a plan again to see current cover and pricing.',
  },
  STU_PRICE_UNAVAILABLE: {
    title: 'We could not get a price for this plan',
    body: 'Try another plan, or come back in a few minutes.',
  },
  STU_KYC_PLAN_NOT_COMMITTED: {
    title: 'Pick your plan again to continue',
  },
  STU_KYC_NOT_AVAILABLE: {
    title: 'This insurer does not need an identity check',
  },
  STU_KYC_MISCONFIGURED: {
    title: 'Something went wrong on our side',
    body: "This isn't about your details. Please try again in a little while.",
  },
  STU_KYC_UPSTREAM_REJECTED: {
    title: "The insurer couldn't match these details",
    body: 'Check them and try again.',
  },
  STU_KYC_CONSENT_REQUIRED: {
    title: 'Your consent is needed to continue',
  },
  STU_KYC_UPSTREAM_UNAVAILABLE: {
    title: "The insurer isn't responding",
    body: 'Nothing you entered has been lost. Try again in a moment.',
  },
  STU_KYC_ALREADY_VERIFIED: {
    title: 'Your identity is already verified',
  },
  STU_KYC_OTP_INVALID: {
    title: "That code didn't work",
    body: 'Check the SMS and enter it again.',
  },
  STU_KYC_OTP_REQUIRED: {
    title: 'This code has expired',
    body: 'Verify your details again to get a new code.',
  },
  STU_KYC_NOT_VERIFIED: {
    title: 'Verify your identity to continue',
  },
  STU_DOCUMENT_INVALID: {
    title: "This file can't be uploaded",
    body: 'Check its type and size, and choose another file.',
  },
  STU_DOCUMENT_REJECTED: {
    title: "The insurer didn't accept this file",
    body: 'Choose another file and upload it again.',
  },
  STU_DOCUMENT_QUESTION_INVALID: {
    title: "This file can't be uploaded here",
    body: 'Save this page first, then upload the file.',
  },
  STU_DOCUMENT_STALE: {
    title: 'Please upload your documents again',
    body: 'The insurer needs them again before you confirm.',
  },
  STU_PROPOSAL_REVALIDATION_FAILED: {
    title: "We couldn't recheck your proposal with the insurer",
    body: 'Please try again in a few minutes.',
  },
  STU_KYC_IDENTITY_MISMATCH: {
    title: "Some details don't match your KYC",
  },
  STU_KYC_CARRY_FORWARD_UNCONFIRMED: {
    title: "We couldn't confirm your verification",
    body: 'Please try again.',
  },
  STU_KYC_PLAN_NO_LONGER_AVAILABLE: {
    title: "This plan isn't available for you",
    body: "Based on your verified date of birth, the insurer can't offer this plan. Other plans are ready for you.",
  },
  STU_QUOTE_LOCKED: {
    title: 'This plan has already been paid for',
    body: 'Your payment has gone through, so these details can no longer be changed.',
  },
  STU_PURCHASE_IN_PROGRESS: {
    title: 'You may have already paid',
    body: 'Another purchase for you may already be paid or in progress.',
  },
  STU_PAYMENT_PRECHECK_UNAVAILABLE: {
    title: "Couldn't start the payment",
    body: 'Please try again.',
  },
  STU_QUOTE_EXPIRED: {
    title: 'This quote has expired',
    body: 'Please get a fresh quote.',
  },
  STU_FANOUT_BUDGET_EXCEEDED: {
    title: "You've checked a lot of quotes",
    body: 'Try again in a little while.',
  },
  RBAC_ACCESS_DENIED: {
    title: "You don't have access to this",
    body: 'Sign in with the account that started this application.',
  },
};

const UNLISTED_ERROR_COPY: Record<string, IStuErrorCopy> = {
  [STU_UNLISTED_ERROR_CODES.PROPOSER_IDENTITY_MISMATCH]: {
    title: "This doesn't match the date of birth on your Plum profile",
    body: 'Use the same date of birth your employer has on record for you.',
  },
};

export const STU_ERROR_COPY: Record<string, IStuErrorCopy> = {
  ...KNOWN_ERROR_COPY,
  ...UNLISTED_ERROR_COPY,
};
